package meineglasfaser.frontend.admin.pages.shop

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import meineglasfaser.api.CustomerType
import meineglasfaser.api.ProductAdmin
import meineglasfaser.api.ProductState
import meineglasfaser.api.YaTableOptions
import meineglasfaser.api.routes.AdminRoutes
import meineglasfaser.api.routes.PrefetchRoutes
import meineglasfaser.api.routes.PrefetchedEntry
import meineglasfaser.api.ubl.UBLAmount
import meineglasfaser.frontend.admin.components.widgets.YaTable
import meineglasfaser.frontend.admin.components.widgets.named
import meineglasfaser.frontend.admin.components.widgets.sortedAsc
import meineglasfaser.frontend.common.BackendApi
import meineglasfaser.frontend.common.components.widgets.*
import meineglasfaser.frontend.common.components.yaform.*
import meineglasfaser.frontend.common.setJsonBody
import meineglasfaser.frontend.common.withAuth
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*

@Page
@Composable
fun ProductsPage(ctx: PageContext) {
    val networks = prefetch(PrefetchRoutes.Networks())
    val addonGroups = prefetch(PrefetchRoutes.AddonGroups())
    val taxCategories = prefetch(PrefetchRoutes.TaxCategories())

    var version by remember { mutableIntStateOf(0) }
    var showCreateModal by remember { mutableStateOf(false) }
    var showUpdateModal by remember { mutableStateOf<ProductAdmin?>(null) }

    YaTable(
        header = { H1 { Text("Produkte") } },
        idProperty = ProductAdmin::id,
        columns = listOf(
            ProductAdmin::nid named "P.-Nr.",
            ProductAdmin::state named "Status",
            ProductAdmin::title named "Title",
            ProductAdmin::audience named "Zielgruppe",
            ProductAdmin::assignedTo named "Zugewiesen zu",
            ProductAdmin::maxQuantity named "Max. Menge",
            ProductAdmin::monthlyPrice named "Monatlicher Preis",
            ProductAdmin::yearlyPrice named "Jährlicher Preis",
            ProductAdmin::oncePrice named "Einmaliger Preis",
        ),
        initialOptions = YaTableOptions.DEFAULT.copy(
            orderBy = listOf(
                ProductAdmin::nid.sortedAsc(),
            ),
        ),
        fetch = { options ->
            BackendApi.post(AdminRoutes.Products()) {
                withAuth()
                setJsonBody(options)
            }
        },
        onNewEntry = { showCreateModal = true },
        onViewEntry = { showUpdateModal = it },
        mapValue = { col, value ->
            when (col) {
                "assignedTo" -> {
                    val assignments = value as List<Pair<String, String?>>
                    assignments.map { (networkId, addonGroupId) ->
                        val networkLabel = networks.find { it.id == networkId }?.label ?: "Unbekanntes Netzwerk"
                        if (addonGroupId != null) {
                            val addonGroupLabel = addonGroups.find { it.id == addonGroupId }?.label ?: "Unbekannte Addon-Gruppe"
                            "$networkLabel und $addonGroupLabel"
                        } else {
                            networkLabel
                        }
                    }
                }
                else -> value
            }
        },
        version = version,
    )

    if (showCreateModal) {
        CreateProductModal(networks, addonGroups, taxCategories) {
            showCreateModal = false
            version++
        }
    }
    showUpdateModal?.let { product ->
        UpdateProductModal(product, networks, addonGroups, taxCategories) {
            showUpdateModal = null
            version++
        }
    }
}

@Composable
fun CreateProductModal(
    networks: List<PrefetchedEntry>,
    addonGroups: List<PrefetchedEntry>,
    taxCategories: List<PrefetchedEntry>,
    onHide: () -> Unit,
) {
    val initial = remember(taxCategories) {
        val standard = taxCategories.find { it.label.contains("Normalsteuersatz") }
        val taxCategoryId = standard?.id ?: taxCategories.firstOrNull()?.id ?: ""
        ProductAdmin(
            orderPos = 0,
            state = ProductState.DRAFT,
            title = "",
            summary = "",
            details = "",
            audience = emptyList(),
            maxQuantity = 1,
            monthlyPrice = UBLAmount.ZERO_EUR,
            monthlyTaxCategoryId = taxCategoryId,
            yearlyPrice = UBLAmount.ZERO_EUR,
            yearlyTaxCategoryId = taxCategoryId,
            oncePrice = UBLAmount.ZERO_EUR,
            onceTaxCategoryId = taxCategoryId,
            assignedTo = emptyList(),
        )
    }
    Modal(onHide, size = ModalSize.XL) {
        YaForm(
            initial = initial,
            onSubmitScope = { data ->
                val body = ProductAdmin(
                    id = null,
                    nid = null,
                    orderPos = data.int(ProductAdmin::orderPos),
                    state = ProductState.valueOf(data.string(ProductAdmin::state)),
                    title = data.string(ProductAdmin::title),
                    summary = data.string(ProductAdmin::summary),
                    details = data.string(ProductAdmin::details),
                    audience = data.list(ProductAdmin::audience),
                    maxQuantity = data.int(ProductAdmin::maxQuantity),
                    monthlyPrice = UBLAmount.eur(data.string(ProductAdmin::monthlyPrice)),
                    monthlyTaxCategoryId = data.string(ProductAdmin::monthlyTaxCategoryId),
                    yearlyPrice = UBLAmount.eur(data.string(ProductAdmin::yearlyPrice)),
                    yearlyTaxCategoryId = data.string(ProductAdmin::yearlyTaxCategoryId),
                    oncePrice = UBLAmount.eur(data.string(ProductAdmin::oncePrice)),
                    onceTaxCategoryId = data.string(ProductAdmin::onceTaxCategoryId),
                    assignedTo = data.list(ProductAdmin::assignedTo),
                )
                BackendApi.post(AdminRoutes.Products.New()) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        onHide()
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Anlegen des Produkts: ${error.message}"))
                    }
                )
            }
        ) {
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Produkt anlegen") }
            }
            ProductAdminInputs(
                networks = networks,
                addonGroups = addonGroups,
                taxCategories = taxCategories,
            )
            ModalFooterButtons(onHide, submitLabel = "Anlegen")
        }
    }
}

@Composable
fun UpdateProductModal(
    product: ProductAdmin,
    networks: List<PrefetchedEntry>,
    addonGroups: List<PrefetchedEntry>,
    taxCategories: List<PrefetchedEntry>,
    onHide: () -> Unit,
) {
    Modal(onHide, size = ModalSize.XL) {
        YaForm(
            initial = product,
            onSubmitScope = { data ->
                val body = ProductAdmin(
                    id = product.id,
                    nid = product.nid,
                    orderPos = data.int(ProductAdmin::orderPos),
                    state = ProductState.valueOf(data.string(ProductAdmin::state)),
                    title = data.string(ProductAdmin::title),
                    summary = data.string(ProductAdmin::summary),
                    details = data.string(ProductAdmin::details),
                    audience = data.list(ProductAdmin::audience),
                    maxQuantity = data.int(ProductAdmin::maxQuantity),
                    monthlyPrice = UBLAmount.eur(data.string(ProductAdmin::monthlyPrice)),
                    monthlyTaxCategoryId = data.string(ProductAdmin::monthlyTaxCategoryId),
                    yearlyPrice = UBLAmount.eur(data.string(ProductAdmin::yearlyPrice)),
                    yearlyTaxCategoryId = data.string(ProductAdmin::yearlyTaxCategoryId),
                    oncePrice = UBLAmount.eur(data.string(ProductAdmin::oncePrice)),
                    onceTaxCategoryId = data.string(ProductAdmin::onceTaxCategoryId),
                    assignedTo = data.list(ProductAdmin::assignedTo),
                )
                BackendApi.post(AdminRoutes.Products.ById(product.id!!)) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        onHide()
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Aktualisieren des Produkts: ${error.message}"))
                    }
                )
            },
        ) {
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Produkt bearbeiten") }
            }
            ProductAdminInputs(
                networks = networks,
                addonGroups = addonGroups,
                taxCategories = taxCategories,
            )
            ModalFooterButtons(onHide, submitLabel = "Speichern")
        }
    }
}

@Composable
private fun YaFormScope<ProductAdmin>.ProductAdminInputs(
    networks: List<PrefetchedEntry>,
    addonGroups: List<PrefetchedEntry>,
    taxCategories: List<PrefetchedEntry>,
) {
    Div({ classes("row") }) {
        Div({ classes("col-12", "col-lg-6") }) {
            Div({ classes("row") }) {
                Div({ classes("col-12", "col-md-6")}) {
                    YaFormNumberInput(
                        prop = ProductAdmin::nid,
                        label = { Text("P.-Nr.") },
                        min = 0,
                        required = true,
                        disabled = true,
                    )
                }
                Div({ classes("col-12", "col-md-6")}) {
                    YaFormDropdown(
                        prop = ProductAdmin::state,
                        label = { Text("Status") },
                        options = ProductState.entries.map { it to it.name },
                        required = true,
                    )
                }
                Div({ classes("col-12")}) {
                    YaFormTextInput(
                        prop = ProductAdmin::title,
                        label = { Text("Titel") },
                        type = InputType.Text,
                        required = true,
                    )
                    YaFormTextArea(
                        prop = ProductAdmin::summary,
                        label = { Text("Zusammenfassung") },
                        rows = 3,
                        required = false,
                    )
                    YaFormTextArea(
                        prop = ProductAdmin::details,
                        label = { Text("Details") },
                        rows = 5,
                        required = false,
                    )
                }
                Div({ classes("col-12", "col-md-4") }) {
                    YaFormTextInput(
                        prop = ProductAdmin::monthlyPrice,
                        type = InputType.Text,
                        label = { Text("Monatlicher Preis") },
                        required = true,
                    )
                }
                Div({ classes("col-12", "col-md-8") }) {
                    YaFormDropdown(
                        prop = ProductAdmin::monthlyTaxCategoryId,
                        label = { Text("Monatliche Steuerkategorie") },
                        options = taxCategories.map { it.id to it.label },
                        required = true,
                    )
                }
                Div({ classes("col-12", "col-md-4") }) {
                    YaFormTextInput(
                        prop = ProductAdmin::yearlyPrice,
                        type = InputType.Text,
                        label = { Text("Jährlicher Preis") },
                        required = true,
                    )
                }
                Div({ classes("col-12", "col-md-8") }) {
                    YaFormDropdown(
                        prop = ProductAdmin::yearlyTaxCategoryId,
                        label = { Text("Jährliche Steuerkategorie") },
                        options = taxCategories.map { it.id to it.label },
                        required = true,
                    )
                }
                Div({ classes("col-12", "col-md-4") }) {
                    YaFormTextInput(
                        prop = ProductAdmin::oncePrice,
                        type = InputType.Text,
                        label = { Text("Einmaliger Preis") },
                        required = true,
                    )
                }
                Div({ classes("col-12", "col-md-8") }) {
                    YaFormDropdown(
                        prop = ProductAdmin::onceTaxCategoryId,
                        label = { Text("Einmalige Steuerkategorie") },
                        options = taxCategories.map { it.id to it.label },
                        required = true,
                    )
                }
            }
        }
        Div({ classes("col-12", "col-lg-6") }) {
            Div({ classes("row") }) {
                Div({ classes("col-12", "col-md-6")}) {
                    YaFormNumberInput(
                        prop = ProductAdmin::maxQuantity,
                        label = { Text("Max. Menge") },
                        min = 0,
                        required = true,
                    )
                }
                Div({ classes("col-12", "col-md-6")}) {
                    YaFormNumberInput(
                        prop = ProductAdmin::orderPos,
                        label = { Text("Position") },
                        min = 0,
                        required = true,
                    )
                }
                Div({ classes("col-12") }) {
                    YaFormStringListInput(
                        prop = ProductAdmin::audience,
                        label = { Text("Zielgruppe") },
                        options = CustomerType.entries.map { it.name to it.name },
                    )
                    ProductAssignmentInout(
                        prop = ProductAdmin::assignedTo,
                        networks = networks,
                        addonGroups = addonGroups,
                    )
                }
            }
        }
    }
}

@Composable
private fun YaFormScope<ProductAdmin>.ProductAssignmentInout(
    prop: YaFormProperty<ProductAdmin>,
    networks: List<PrefetchedEntry>,
    addonGroups: List<PrefetchedEntry>,
) {
    val value = listValueOf<Pair<String, String?>>(prop)

    Div(attrs = { classes("product-assignment-container") }) {
        B { Text("Produktzuweisungen") }
        Br()
        Small { Text("Wenn Addon-Gruppe leer, dann Hauptprodukt, ansonsten Nebenprodukt") }
        Br()
        value.forEachIndexed { index, (networkId, addonGroupId) ->
            Div(attrs = { classes("row") }) {
                Div(attrs = { classes("col-6")}) {
                    Select(
                        attrs = {
                            required()
                            onInput { event ->
                                val newNetworkId = event.value
                                val updatedList = value.toMutableList()
                                updatedList[index] = (newNetworkId ?: "") to addonGroupId
                                state.data[prop.name] = updatedList
                            }
                        }
                    ) {
                        Option(value = "", attrs = {
                            if (addonGroupId == null) selected()
                        }) {
                            Text("(nicht zugewiesen)")
                        }
                        networks.forEach { network ->
                            Option(value = network.id, attrs = {
                                if (network.id == networkId) selected()
                            }) {
                                Text(network.label)
                            }
                        }
                    }
                }
                Div(attrs = { classes("col-5")}) {
                    Select(
                        attrs = {
                            onInput { event ->
                                val newAddonGroupId = event.value
                                val updatedList = value.toMutableList()
                                updatedList[index] = networkId to (newAddonGroupId ?: "")
                                state.data[prop.name] = updatedList
                            }
                        }
                    ) {
                        Option(value = "", attrs = {
                            if (addonGroupId == null) selected()
                        }) {
                            Text("(nicht zugewiesen)")
                        }
                        addonGroups.forEach { addonGroup ->
                            Option(value = addonGroup.id, attrs = {
                                if (addonGroup.id == addonGroupId) selected()
                            }) {
                                Text(addonGroup.label)
                            }
                        }
                    }
                }
                Div(attrs = { classes("col-1")}) {
                    Button(attrs = {
                        type(ButtonType.Button)
                        onClick {
                            val updatedList = value.toMutableList()
                            updatedList.removeAt(index)
                            state.data[prop.name] = updatedList
                        }
                    }) {
                        Text("X")
                    }
                }
            }
        }

        Button(attrs = {
            type(ButtonType.Button)
            onClick {
                val updatedList = value.toMutableList()
                updatedList.add("" to null)
                state.data[prop.name] = updatedList
            }
        }) {
            Text("Neue Zeile")
        }
    }
}
